import {
    GsInitParams,
    SessionParams,
    ActiveSessionInfo,
    ActiveSessionResultEvent,
    SessionProgressUpdateEvent,
    SessionState,
    AppLaunchMode,
    GS_EVENTS,
    GridSession,
    ServerError,
    SESSIONMODIFY_ACTION,
    Usage,
    AppLevelProtocol,
    AdUpdate,
    SessionUpdate,
    ServerInfo
} from "./interfaces";
import {
    AuthInfo,
    ErrorDetails,
    GetHexString,
    getNewGuid,
    PlatformDetails,
    PackageVersion,
    TracingManager,
    NetworkType,
    Log,
    LegacyLogEmitter,
    TelemetryEventIds,
    TelemetryEventPayload
} from "./dependencies";
import { GsErrorCode } from "./gserrorcode";
import { GridServer_GameLaunch_Request, ScTelemetryHandler } from "./gstelemetryhandler";
import { TracingOperation } from "./tracing";
import {
    ServerResponse,
    SessionControlImpl,
    SessionRequestType,
    getErrorCodeDescription
} from "./sessioncontrolimpl";
import {
    InitializeParameters,
    Session,
    SessionParameters,
    SessionStartResumeResult
} from "./sessioncontrol";
import { isCancelCode } from "./sessioncontrolutils";

const LOGTAG = "gridserver";

/**
 * The interface defines the protocol to connect to PM.
 *
 * GridServer is the proxy class which implements the protocol and provides the actual ability to communicate with PM for session setup and teardown.
 * And it could be instantiated as: const gridServer: GridServer = new GridServer(platformDetails, emitEventsSynchronously);
 *
 * Client needs to register listeners for GS_EVENTS as described in interface.ts file for the events it emits.
 *
 * Sample Usage:
 * const gridServer = new GridServer(platformDetails, emitEventsSynchronously);
 * gridServer.initialize({
 *     deviceOs: "WINDOWS",
 *     deviceOsVer: "11.0.22000.0",
 *     deviceType: "DESKTOP",
 *     clientIdentification: "GFN-PC",
 *     clientVersion: "24.0",
 *     clientAppVersion: "2.0.50.104",
 *     clientStreamer: "WEBRTC",
 *     browserType: "CHROME",
 *     deviceHashId: "1234567890",
 *     serverAddress: "server-address.nvidiagrid.net",
 *     autoTokenCallback: <callback function>,
 *     clientHeaders: {
 *         "nv-browser-version": "123.0.1234.12"
 *     },
 *     ...
 * });
 * gridServer.setAuthInfo(AuthInfo.JARVIS, "some token");
 *
 * gridServer.addListener(GS_EVENTS.ACTIVE_SESSIONS_RESULT, (eventData: ActiveSessionResultEvent) => {});
 * gridServer.addListener(GS_EVENTS.PROGRESS_UPDATE, (eventData: SessionProgressUpdateEvent) => {});
 * gridServer.addListener(GS_EVENTS.GET_SESSION_RESULT, (eventData: GetSessionResult) => {});
 *
 * gridServer.putOrPostSession({
 *     streamParams: [...],
 *     appId: 100000000,
 *     shortName: "Fortnite (Epic)",
 *     ...
 * }, SESSIONMODIFY_ACTION.UNKNOWN); // Start a session
 * gridServer.getSession(some_session_id, true); // Queue progress detail will be emitted through the GS_EVENTS.PROGRESS_UPDATE event
 * gridServer.getAllActiveSessions(); // Retrieve current session of the user, session detail will be emitted through the GS_EVENTS.GET_SESSION_RESULT
 * gridServer.sendDeleteRequest(some_session_id); // Delete the existing session
 *
 * gridServer.uninitialize(); // Release all resources
 */
declare interface IGridServer {
    /**
     * Initialize the grid server.
     * NOTE: should be called before any network operations:
     *         - getAllActiveSessions
     *         - putOrPostSession
     *         - getSession
     *         - sendDeleteRequest
     * @param initParams - interface: GsInitParams
     */
    initialize(initParams: GsInitParams): void;

    /**
     * This method should be called before recreating GridServer object
     **/
    uninitialize(): void;

    /**
     * Adds a function which should be invoked for a particular event.
     * Any number of handlers can be registered for an event.
     * @param eventname - the event type for which the corresponding handler to be invoked.
     * @param handler - listener function to be invoked when the event is emitted.
     **/
    addListener(eventname: string, handler: Function): void;

    /**
     *  Removes an handler for a particular event.
     * @param eventname - the event type for which the corresponding handler  has to be removed.
     * @param handler - listener function to be removed.
     **/
    removeListener(eventname: string, handler: Function): void;

    /**
     * Update telemetry event ids. Without calling the function results in
     * telemetry ID field to be blank.
     * NOTE: should be called before putOrPostSession.
     * @param telemetryEventIds - interface: TelemetryEventIds
     */
    updateTelemetryEventIds(telemetryEventIds: TelemetryEventIds): void;

    /**
     * Gets the current active sessions for the user.
     * This is an asynchronous API, upon completion ACTIVE_SESSIONS_RESULT is emitted for results.
     **/
    getAllActiveSessions(): void;

    /**
     * This method returns the current sessionId.
     * NOTE: the session id is set/reset on putOrPostSession calls and will be empty until one is executed.
     * Ideally the session ID is the current one, but it will not be expired if the current session expires.
     **/
    getSessionId(): string;

    /**
     * This method returns the current subSessionId.
     * NOTE: the SubSessionId is set/reset on putOrPostSession calls and will be empty until one is executed.
     *       Ideally the SubSessionId is the current one, but it will not be expired if the current SubSession expires.
     **/
    getSubSessionId(): string;

    /**
     * This method can be used to resume (PUT) or start (POST) a session.
     * Result is returned in promise with GridSession object.
     * @param startParams - object of SessionParams object.
     * @param action - value from SESSIONMODIFY_ACTION for resume or starting session.
     * @param sessionId - sessionId in case of resume
     * @throws ServerError in case of failure
     **/
    putOrPostSession(
        startParams: SessionParams,
        action: SESSIONMODIFY_ACTION,
        sessionId: string | undefined
    ): Promise<GridSession | undefined>;

    /**
     * This method can be used to get a session details belonging to a session id for resume scenario or during polling.
     * Pass isPoll flag true when post request is made for session setup.
     * Result is returned in promise with GridSession object.
     * @param sessionId - sessionId in case of resume.
     * @param isPoll - used in post request.
     **/
    getSession(_sessionId: string, isPoll: boolean): Promise<GridSession | undefined>;

    /**
     *  For certain sessions GFN servers requires clients to display Ads to users.
     *  The Ads information are provided in the SessionUpdate event during getSession(isPoll = true)
     *  Application is expected to inform the state of Ad Play to GridServer module during session setup process.
     *  AdAction:
     *     START  - application started displaying the ad.
     *     PAUSE  - if the ad play is paused because of user navigating away from application.
     *     RESUME - ad play continuation after being paused.
     *     FINISH - ad play completed.
     *     CANCEL - User stopped ad play or client failed to play the ad.
     *  Note:
     *   Valid Ad actions for current AdState are
     *      CurrentState - AdAction
     *      STARTED  -> PAUSE|FINISH|CANCEL
     *      PAUSED   -> RESUME|CANCEL
     *      RESUMED  -> PAUSE|FINISH|CANCEL
     *      FINISHED -> None
     *      CANCELLED -> None
     *  This API is valid only if a getSession(isPoll = true) is in progress. After all the Ad play is completed and server is ready for streaming the getSession promise will be resolved.
     *  This API can throttle the ads update request to server if there are too many pause/resume updates for an Ad.
     *  Returns GsErrorCode.Success if the ad state update is successfully accepted.
     *  @param  adUpdate: AdUpdate to be sent to the server. Refer to AdUpdate documentation for the various parameters to be passed.
     *     Note: 1. Ad play can be canceled on the client device for multiple reasons,
     *           In few cases the session might be terminated on server which results in getSession promise rejection
     *           and in few cases session will transition to ready for streaming (getSession promise resolution) only after waiting for minimum duration.
     *           Clients are expected
     *           2. Applications can keep invoking this API without need to monitor the state of the Ad received from server.
     *              Multiple AdUpdates might be sent to server in one request. This usually happens when transitioning from one Ad to another.
     *              Ad1 action FINISH and Ad2 action START will be combined and sent to server in one request.
     *           3. This API can internally throttle the sequential PAUSE-RESUME actions.
     */
    updateAdState(adUpdate: AdUpdate): GsErrorCode;

    /**
     * This method can be used to send a delete request for a given session id.
     * Pass isPoll flag true when post request is made for session setup.
     * Result is returned in promise of type void.
     * @param sessionId - sessionId to delete
     * @note Will not cancel active operations.
     * @note If putOrPostSession or getSession is in progress, should first call cancelSessionSetup
     **/
    sendDeleteRequest(sessionId: string): Promise<void>;

    /**
     * Set the client's authorization information.
     * NOTE: initialize must be called before using this API.
     *       Should be called before the first use of APIs that require authentication with the server.
     *         - getAllActiveSessions
     *         - putOrPostSession
     *         - getSession
     *         - sendDeleteRequest
     *       If not called, Jarvis authentication will be assumed.
     *       In the event of authentication related errors, can be called again to update the existing authentication information.
     * @param authInfo - object
                     { type: enum<auth method for server communication>
                       token: string<token to authenticate server communication>}
     **/
    setAuthInfo(authInfo: AuthInfo): void;

    /**
     * This method can be used to cancel in progress session setup.
     * Should be called only after putOrPostSession or getSession with isPoll = true, before they resolve.
     * Ongoing APIs will throw with either SessionSetupCancelled or SessionSetupCancelledDuringQueuing.
     * @note Will not send DELETE request for the session.
     * @note Should call sendDeleteRequest following putOrPostSession or getSession resolution if the session should be deleted.
     **/
    cancelSessionSetup(): void;

    /**
     * Get zone name.
     * NOTE: will be empty until getSession or putOrPostSession gets called.
     */
    getZoneName(): string;

    /**
     * Get zone address.
     * NOTE: will be empty until getSession or putOrPostSession gets called.
     */
    getZoneAddress(): string;

    /**
     * Get GPU type.
     * NOTE: will be empty until getSession or putOrPostSession gets called.
     */
    getGpuType(): string;

    /**
     * Update telemetry network status. Default: NetworkType.UNKNOWN
     * @param network - interface: NetworkType
     */
    setNetworkType(network: NetworkType): void;
}

/**
 * Converts the legacy SessionParams to SessionParameters so that SessionControlImpl functions can be used in this class.
 * @param startParams - legacy SessionParams
 * @returns SessionParameters to be passed to base class.
 */
function convertSessionParamsToSessionParameters(startParams: SessionParams): SessionParameters {
    // Few fields of SessionParams like shortName, networkSessionId, sessionId are not needed.
    // Rest all fields match, except for monitorSettings which is mandatory in SessionParameters.
    const sessionParameters: SessionParameters = {
        monitorSettings: [],
        ...startParams
    };

    // If monitorSettings were already given, don't add streamParams
    if (sessionParameters.monitorSettings.length === 0) {
        for (const stream of startParams.streamParams ?? []) {
            sessionParameters.monitorSettings.push({
                heightInPixels: stream.height,
                framesPerSecond: stream.fps,
                widthInPixels: stream.width
            });
        }
    }
    return sessionParameters;
}

/**
 * Converts the Session interfaces returned by SessionControl library to GridSession object.
 * @param session - new Session interface
 * @returns legacy GridSession object.
 */
function convertSessionToGridSession(session: Session): GridSession {
    // New Session interface just provides an array of connectionInfo.
    // Legacy GridSession requires signalConnectionInfo to be set.
    // Set the connectionInfo.usage.SIGNALING details to GridSession.signalConnectionInfo
    // Note: Only ragnarok uses the signaling port, all other platforms are using the GridSession.mediaConnectionInfo which includes.
    // GridSession.mediaConnectionInfo contains two signaling ports from server in non browser case.
    const signalConnectionInfo: ServerInfo = {
        ip: "",
        port: 0,
        protocol: ""
    };

    for (const connectionInfo of session.connectionInfo) {
        if (connectionInfo.usage === Usage.SIGNALING) {
            signalConnectionInfo.ip = connectionInfo.ip;
            signalConnectionInfo.port = connectionInfo.port;
            signalConnectionInfo.protocol =
                connectionInfo.appLevelProtocol === AppLevelProtocol.HTTPS ? "https" : "http";
            break;
        }
    }

    const gridSession: GridSession = {
        signalConnectionInfo: signalConnectionInfo,
        mediaConnectionInfo: session.connectionInfo,
        streamInfo: [],
        clientLocale: "",
        ...session
    };

    for (const monitor of session.monitorSettings) {
        gridSession.streamInfo.push({
            width: monitor.widthInPixels,
            height: monitor.heightInPixels,
            fps: monitor.framesPerSecond,
            sdrHdrMode: monitor.sdrHdrMode
        });
    }

    return gridSession;
}

/**
 * Converts Session array to legacy ActiveSessionInfo object.
 * @param activeSessions - array of Session objects
 * @returns Array of legacy ActiveSessionInfo
 */
function convertSessionsToActiveSessionInfo(activeSessions?: Session[]): ActiveSessionInfo[] {
    let sessionList: ActiveSessionInfo[] = [];
    if (activeSessions) {
        for (const session of activeSessions) {
            let activeSession: ActiveSessionInfo = {
                sessionId: session.sessionId,
                appId: session.appId,
                state: session.state,
                appLaunchMode: session.appLaunchMode
            };
            sessionList.push(activeSession);
        }
    }
    return sessionList;
}

export class GridServer extends SessionControlImpl implements IGridServer {
    protected subSessionId: string = "";
    private zoneName?: string;
    private zoneAddress?: string;
    private subSessionsIdMap: Map<string, string> = new Map();
    private clearLogCallback: boolean = false;
    private putOrPostRequestController?: AbortController;
    private getSessionRequestController?: AbortController;
    private gpuType?: string;
    private launchRequestInfoMap: Map<string, GridServer_GameLaunch_Request> = new Map();
    protected eventEmitter: LegacyLogEmitter;
    private telemetryCallback = (telemetry: TelemetryEventPayload) => {
        this.eventEmitter.emit(GS_EVENTS.TELEMETRY_EVENT, telemetry);
    };
    private sessionUpdateHandler = (sessionUpdate: SessionUpdate) => {
        this.sendSessionUpdateEvent(sessionUpdate);
    };

    constructor(platformDetails?: PlatformDetails, emitEventsSynchronously?: boolean) {
        super();
        this.platformDetails = platformDetails;

        this.eventEmitter = new LegacyLogEmitter(GS_EVENTS.LOG_EVENT, emitEventsSynchronously);
        // In Ragnarok 2.0 or SessionControl 2.0 usage, clients will use registerLogCallback for receiving logs.
        // For legacy use case we need to emit events from GridServer and GridApp.
        // In Ragnarok 1.0, GridApp would have registered a callback with the logger.
        // Only when clients are using standalone GridServer will logs be emitted from GridServer.
        if (!Log.isCallbackRegistered()) {
            this.clearLogCallback = true;
            Log.setLogCallback(this.eventEmitter.onLogEvent.bind(this.eventEmitter));
        }

        // In legacy mode we need to emit an event, register a callback which will emit an event.
        ScTelemetryHandler.configure({ option: this.telemetryCallback });
    }

    public initialize(initParams: GsInitParams) {
        Log.i("{6bae601}", "{6802390}", PackageVersion);
        // Convert legacy init parameters to new SessionControl::InitializeParameters
        // Main differences are:
        //   deviceHashId is renamed to deviceId, clientStreamer is string.
        //   ClientIdentification and ClientVersion will be hardcoded in SessionControl.
        //   appUserAgent, ecomEnabled, authTokenCallback is removed as they are no longer needed in GFN.
        const initializeParameters: InitializeParameters = {
            deviceId: initParams.deviceHashId ?? "",
            ...initParams,
            clientStreamer: initParams.clientStreamer.toString()
        };
        // initialize base class.
        super.init(initializeParameters);

        ScTelemetryHandler.resetCache();
        Log.d("{6bae601}", "{5af3344}");
    }

    /**
     * This method should be called before recreating GridServer object
     **/
    public uninitialize() {
        this.cancelSessionSetup();
        if (this.clearLogCallback) {
            // Clear the registered callback.
            Log.setLogCallback();
        }
        this.eventEmitter.removeAllListeners();
    }

    addListener(eventname: string, handler: Function): void {
        this.eventEmitter.addListener(eventname, handler);
    }

    removeListener(eventname: string, handler: Function): void {
        this.eventEmitter.removeListener(eventname, handler);
    }

    public updateTelemetryEventIds(telemetryEventIds: TelemetryEventIds) {
        ScTelemetryHandler.updateTelemetryEventIds(telemetryEventIds);
    }

    /**
     * This method can be used to get all active sessions for current logged in user.
     * Expect ACTIVE_SESSIONS_RESULT event for results.
     **/
    public getAllActiveSessions(): void {
        const request = () => {
            this.addTracingAttributesToSpan();
            return this.getAllActiveSessionsImpl();
        };
        TracingManager.getInstance().createSpan(TracingOperation.GetSessionList, request);
    }

    private async getAllActiveSessionsImpl(): Promise<void> {
        Log.d("{6bae601}", "{1aef200}");
        if (!this.serverAddress) {
            let result: ActiveSessionResultEvent = {
                sessionList: [],
                error: {
                    code: GsErrorCode.GridServerNotInitialized,
                    description: "GridServer not initialized"
                }
            };
            this.eventEmitter.emit(GS_EVENTS.ACTIVE_SESSIONS_RESULT, result);
            this.addResultToSpan(GsErrorCode.GridServerNotInitialized);
            return;
        }
        let url = this.protocol + this.serverAddress + "/v2/session";

        await this.performSessionRequest(SessionRequestType.GET_ACTIVE_SESSIONS, url, "")
            .then((response: ServerResponse) => {
                let result: ActiveSessionResultEvent = {
                    sessionList: convertSessionsToActiveSessionInfo(response.activeSessions)
                };
                if (response.result != GsErrorCode.Success) {
                    let err: ErrorDetails = {
                        code: response.result,
                        description: getErrorCodeDescription(response.result)
                    };
                    result.error = err;
                    Log.e("{6bae601}", "{9d2bb75}", GetHexString(err.code));
                } else {
                    Log.d("{6bae601}", "{081857f}");
                }
                this.addResultToSpan(response.result);
                this.eventEmitter.emit(GS_EVENTS.ACTIVE_SESSIONS_RESULT, result);
            })
            .catch(error => {
                Log.e("{6bae601}", "{63f9b57}", error.message);
                Log.e("{6bae601}", "{94d55bb}", error, error.stack);
                ScTelemetryHandler.sendExceptionEvent(
                    error,
                    "getAllActiveSessions",
                    LOGTAG + ".ts",
                    0,
                    0,
                    true
                );

                let result: ActiveSessionResultEvent = {
                    sessionList: [],
                    error: {
                        code: GsErrorCode.DOMExceptionInSessionControl,
                        description: getErrorCodeDescription(
                            GsErrorCode.DOMExceptionInSessionControl
                        )
                    }
                };
                this.addResultToSpan(GsErrorCode.DOMExceptionInSessionControl);
                this.eventEmitter.emit(GS_EVENTS.ACTIVE_SESSIONS_RESULT, result);
            });

        Log.d("{6bae601}", "{a9e5439}");
    }

    protected resetSubSessionId(subSessionId: string, sessionId: string = "") {
        Log.d("{6bae601}", "{1e04fe6}", sessionId, subSessionId);
        this.subSessionId = subSessionId;
        ScTelemetryHandler.setSessionId(sessionId);
        ScTelemetryHandler.setSubSessionId(subSessionId);
        const value = this.subSessionsIdMap.get(subSessionId);
        if (value === undefined || value === "") {
            this.subSessionsIdMap.set(subSessionId, sessionId);
        } else {
            Log.w("{6bae601}", "{7dd264b}", sessionId);
        }
    }

    /**
     * This method returns the current sessionId.
     **/
    public getSessionId(): string {
        return this.subSessionsIdMap.get(this.subSessionId) ?? "";
    }

    /**
     * This method returns the current subSessionId.
     **/
    public getSubSessionId(): string {
        return this.subSessionId;
    }

    /**
     * This method can be used to resume (PUT) or start (POST) a session.
     * Result is returned in promise with GridSession object.
     * @param startParams - object of SessionParams object.
     * @param action - value from SESSIONMODIFY_ACTION for resume or starting session.
     * @param sessionId - sessionId in case of resume
     **/
    public putOrPostSession(
        startParams: SessionParams,
        action: SESSIONMODIFY_ACTION,
        sessionId: string | undefined
    ): Promise<GridSession | undefined> {
        const request = () => {
            this.addTracingAttributesToSpan();
            return this.putOrPostSessionImpl(startParams, action, sessionId);
        };
        return TracingManager.getInstance().createSpan<GridSession | undefined>(
            sessionId ? TracingOperation.PutSession : TracingOperation.PostSession,
            request
        );
    }

    private putOrPostSessionImpl(
        startParams: SessionParams,
        action: SESSIONMODIFY_ACTION,
        sessionId: string | undefined
    ): Promise<GridSession | undefined> {
        const isResume = !!sessionId;
        // todo: the resetSubSessionId is a messed up function, fix it in future CLs.
        this.resetSubSessionId(getNewGuid(), sessionId);
        if (!this.serverAddress) {
            const err = {
                code: GsErrorCode.GridServerNotInitialized,
                description: "GridServer not initialized"
            };
            return this.addErrorAttributesAndReject(err);
        }
        if (this.putOrPostRequestController) {
            const err = {
                code: GsErrorCode.PutOrPostInProgress,
                description: "Previous PutOrPost call is still in progress"
            };
            return this.addErrorAttributesAndReject(err);
        }

        ScTelemetryHandler.setCmsId(startParams.appId);

        // todo: The cache for these zone variables should be directly tied to the lifetime of a session, not global in GridServer
        this.zoneName = undefined;
        this.zoneAddress = undefined;
        this.gpuType = undefined;

        const launchRequestInfo = ScTelemetryHandler.getGameLaunchRequestEvent(
            this.serverAddress,
            isResume,
            startParams.networkSessionId,
            sessionId
        );

        const sessionParameters: SessionParameters =
            convertSessionParamsToSessionParameters(startParams);
        let url = this.createSessionRequestURL(sessionParameters, sessionId);
        let body = this.getSessionRequestData(sessionParameters, isResume, this.subSessionId);
        let startTime = performance.now();

        return new Promise<GridSession | undefined>((resolve, reject) => {
            const abortController = new AbortController();
            this.putOrPostRequestController = abortController;

            this.performSessionRequest(
                isResume ? SessionRequestType.RESUME_SESSION : SessionRequestType.CREATE_SESSION,
                url,
                body,
                abortController,
                sessionId
            )
                .then((response: ServerResponse) => {
                    launchRequestInfo.launchDuration = Math.round(performance.now() - startTime);

                    // needed to update launchRequestInfo in case of post session.
                    if (response.session?.sessionId) {
                        launchRequestInfo.sessionId = response.session?.sessionId;
                        sessionId = response.session?.sessionId;
                    }
                    // Use response.serverId in case there was an error, since this is our only communication with the PM during this session
                    this.zoneName = response.serverId || this.zoneName;
                    let gridSession: GridSession | undefined;
                    if (response.session) {
                        gridSession = convertSessionToGridSession(response.session);
                        gridSession.subSessionId = this.subSessionId;
                        this.updateSessionZoneInfo(gridSession);
                    }
                    launchRequestInfo.zoneName = this.zoneName ?? "";

                    // After a putOrPostSession call, clients can invoke cancelSessionSetup() to terminate this request which invokes abort on the abortcontroller.
                    // If fetch request is in progress or we are waiting between retries the performSessionRequest and performHttpRequest handles it gracefully.
                    // However if the request was completed and JS is waiting for the next resolve/reject execution the abort signal will not be caught in current execution.
                    // The below check will prevent us from providing the wrong update to clients.
                    if (abortController.signal.aborted && !isCancelCode(response.result)) {
                        response.result = GsErrorCode.SessionSetupCancelled;
                        Log.d("{6bae601}", "{3ec0e11}");
                        // todo: BifrostClientSDK sends delete request in this case, GridServer doesn't.
                        // evaluate how addition of delete request affects android client.
                    }

                    if (response.result == GsErrorCode.Success) {
                        Log.d("{6bae601}", "{30536f4}", sessionId);
                        this.resetSubSessionId(this.getSubSessionId(), sessionId);
                        if (response.session?.state == SessionState.READY_FOR_CONNECTION) {
                            launchRequestInfo.result = GetHexString(GsErrorCode.Success);
                            ScTelemetryHandler.sendGameLaunchRequestEvent(launchRequestInfo);
                        } else {
                            // Else, we would get into getSession() polling. Add to the cache
                            this.launchRequestInfoMap.set(
                                launchRequestInfo.sessionId,
                                launchRequestInfo
                            );
                        }

                        this.addResultToSpan(response.result);
                        resolve(gridSession);
                    } else {
                        if (response.result === GsErrorCode.RequestCancelled) {
                            response.result = GsErrorCode.SessionSetupCancelled;
                        }
                        const err: ServerError = {
                            code: response.result,
                            sessionId: sessionId,
                            description: getErrorCodeDescription(response.result),
                            sessionList: convertSessionsToActiveSessionInfo(response.activeSessions)
                        };

                        Log.e("{6bae601}", "{17f93f3}", GetHexString(err.code));
                        // Send the telemetry for the failing cases as well
                        launchRequestInfo.result = GetHexString(err.code);
                        ScTelemetryHandler.sendGameLaunchRequestEvent(launchRequestInfo);

                        this.addResultToSpan(err.code);
                        reject(err);
                    }
                })
                .catch(error => {
                    // performSessionRequest always resolves, catch here should be an exception in catch block of performSessionRequest or above response handling.
                    let err: ServerError = {
                        code: GsErrorCode.DOMExceptionInSessionControl,
                        sessionId: sessionId,
                        description: getErrorCodeDescription(
                            GsErrorCode.DOMExceptionInSessionControl
                        )
                    };
                    Log.e("{6bae601}", "{4318505}", error.message);
                    Log.e("{6bae601}", "{94d55bb}", error, error.stack);
                    ScTelemetryHandler.sendExceptionEvent(
                        error,
                        "putOrPostSession",
                        LOGTAG + ".ts",
                        0,
                        0,
                        true
                    );

                    // The code flow should be generally structured as
                    //     1. perform all operations
                    //     2. send the required telemetry event
                    //     3. resolve or reject.
                    // If we add more code block between telemetry collection and resolve/reject and it throws an exception then we will end up collecting multiple telemetry events.
                    // The problem in this case would be not the duplicate telemetry but the code implementation.
                    launchRequestInfo.result = GetHexString(err.code);
                    ScTelemetryHandler.sendGameLaunchRequestEvent(launchRequestInfo);
                    this.addResultToSpan(err.code);
                    reject(err);
                });
        }).finally(() => {
            this.putOrPostRequestController = undefined;
        });
    }

    /**
     * This method can be used to get a session details belonging to a session id for resume scenario or during polling.
     * Pass isPoll flag true when post request is made for session setup.
     * Result is returned in promise with GridSession object.
     * @param sessionId - sessionId in case of resume.
     * @param isPoll - used in post request.
     **/
    public getSession(_sessionId: string, isPoll: boolean): Promise<GridSession | undefined> {
        const request = () => {
            this.addTracingAttributesToSpan();
            return this.getSessionImpl(_sessionId, isPoll);
        };
        return TracingManager.getInstance().createSpan<GridSession | undefined>(
            TracingOperation.GetSession,
            request
        );
    }

    private getSessionImpl(_sessionId: string, isPoll: boolean): Promise<GridSession | undefined> {
        if (!this.serverAddress) {
            const err = {
                code: GsErrorCode.GridServerNotInitialized,
                description: "GridServer not initialized"
            };
            return this.addErrorAttributesAndReject(err);
        }

        if (isPoll) {
            // Note: session setup process uses the base class functionalities which resolves with SessionStartResumeResult.
            // the handler of this promise converts the SessionStartResumeResult to either GridSession or throws an err to reject the promise handling.
            return new Promise<SessionStartResumeResult>((resolve, reject) => {
                const sessionUrl = this.getUrlForSession(_sessionId);
                const server = new URL(sessionUrl).hostname;
                // Use the telemetry event cached in putOrPostSession
                let launchRequestInfo: GridServer_GameLaunch_Request | undefined =
                    this.launchRequestInfoMap.get(_sessionId);
                if (!launchRequestInfo) {
                    Log.e("{6bae601}", "{bb9b2e3}", _sessionId);

                    launchRequestInfo = ScTelemetryHandler.getGameLaunchRequestEvent(
                        server,
                        false,
                        "",
                        _sessionId
                    );
                } else {
                    this.launchRequestInfoMap.delete(_sessionId);
                }

                const abortController = new AbortController();
                this.getSessionRequestController = abortController;

                this.sessionSetupData = {
                    resolve: resolve,
                    reject: reject,
                    sessionId: _sessionId,
                    subSessionId: this.subSessionId,
                    sessionUpdateCallback: this.sessionUpdateHandler,
                    lastQueuePosition: Number.MAX_VALUE,
                    isSessionInQueue: false,
                    cancelController: abortController,
                    startTime: performance.now(),
                    launchTelemetryEvent: launchRequestInfo
                };

                // Use the base class function which performs GET session request, processes Ads if required
                // and resolves with the SessionStartResumeResult
                this.performSessionSetupPollingRequest();
            })
                .then((result: SessionStartResumeResult) => {
                    let gridSession: GridSession | undefined;
                    if (result.session) {
                        gridSession = convertSessionToGridSession(result.session);
                        // cache the zone information for getZoneName/getZoneAddress/getGpuType function support
                        this.updateSessionZoneInfo(gridSession);
                    }

                    if (result.code === GsErrorCode.Success && gridSession) {
                        return gridSession;
                    } else {
                        let err: ErrorDetails = {
                            code: result.code,
                            description: getErrorCodeDescription(result.code)
                        };
                        throw err;
                    }
                })
                .finally(() => {
                    // Required in case of exceptions. Will also be handled during promise resolve.
                    this.adsStateInfo.clear();
                    this.adUpdateArray.clear();
                    this.sessionSetupData = undefined;
                });
        } else {
            const url = this.getUrlForSession(_sessionId);
            // one time session info request.
            return this.performSessionRequest(
                SessionRequestType.GET_SESSIONINFO,
                url,
                "",
                undefined,
                _sessionId
            ).then((response: ServerResponse) => {
                this.addResultToSpan(response.result);
                if (response.session) {
                    const gridSession: GridSession = convertSessionToGridSession(response.session);
                    return gridSession;
                } else {
                    let err: ErrorDetails = {
                        code: response.result,
                        description: getErrorCodeDescription(response.result)
                    };
                    throw err;
                }
            });
        }
    }

    /**
     * This method can be used to send a delete request for a given session id.
     * Pass isPoll flag true when post request is made for session setup.
     * Result is returned in promise of type void.
     * @param sessionId - sessionId to delete
     * @note Will not cancel active operations.
     * @note If putOrPostSession or getSession is in progress, should first call cancelSessionSetup
     **/

    public sendDeleteRequest(sessionId: string): Promise<void> {
        const request = () => {
            this.addTracingAttributesToSpan();
            return this.sendDeleteRequestImpl(sessionId);
        };
        return TracingManager.getInstance().createSpan(TracingOperation.DeleteSession, request);
    }

    private sendDeleteRequestImpl(sessionId: string): Promise<void> {
        if (!this.serverAddress) {
            const err = {
                code: GsErrorCode.GridServerNotInitialized,
                description: "Gridserver not initialized"
            };
            return this.addErrorAttributesAndReject(err);
        }
        let url = this.getUrlForSession(sessionId);

        return new Promise<void>((resolve, reject) => {
            Log.d("{6bae601}", "{bbad3ce}", sessionId);
            this.performSessionRequest(
                SessionRequestType.DELETE_SESSION,
                url,
                "",
                undefined,
                sessionId
            )
                .then((response: ServerResponse) => {
                    if (response.result == GsErrorCode.Success) {
                        Log.d("{6bae601}", "{6206c13}", sessionId);
                        resolve();
                    } else {
                        let err: ServerError = {
                            code: response.result,
                            description: getErrorCodeDescription(response.result),
                            sessionId: sessionId
                        };
                        Log.e("{6bae601}", "{4de47e3}", sessionId, GetHexString(response.result));
                        reject(err);
                    }
                    this.addResultToSpan(response.result);
                })
                .catch(error => {
                    const err: ErrorDetails = {
                        code: GsErrorCode.DOMExceptionInSessionControl,
                        description: getErrorCodeDescription(
                            GsErrorCode.DOMExceptionInSessionControl
                        )
                    };
                    Log.e("{6bae601}", "{c4f620c}", error.message);
                    Log.e("{6bae601}", "{94d55bb}", error, error.stack);
                    ScTelemetryHandler.sendExceptionEvent(
                        error,
                        "sendDeleteRequestImpl",
                        LOGTAG + ".ts",
                        0,
                        0,
                        true
                    );
                    reject(err);
                });
        });
    }

    /**
     * This method can be used to cancel in progress session setup.
     * Should be called only after putOrPostSession or getSession with isPoll = true, before they resolve.
     * Ongoing APIs will throw with either SessionSetupCancelled or SessionSetupCancelledDuringQueuing.
     * @note Will not send DELETE request for the session.
     * @note Should call sendDeleteRequest following putOrPostSession or getSession resolution if the session should be deleted.
     * @note If the putOrPostSession or getSession promise-resolve/reject is already invoked but the then/catch block in application is not executed then this API is a no-op.
     *       Applications must handle the session result or non cancellation error and take appropriate action.
     **/
    public cancelSessionSetup() {
        Log.i("{6bae601}", "{c73e640}");

        // cancel in progress session operations
        this.putOrPostRequestController?.abort();
        this.getSessionRequestController?.abort();
        // clear in progress session operations
        this.putOrPostRequestController = undefined;
        this.getSessionRequestController = undefined;
    }

    public getZoneName(): string {
        return this.zoneName ?? "";
    }

    public getZoneAddress(): string {
        return this.zoneAddress ?? "";
    }

    public getGpuType(): string {
        return this.gpuType ?? "";
    }

    private addErrorAttributesAndReject(err: ErrorDetails) {
        this.addResultToSpan(err.code, err.description);
        return Promise.reject(err);
    }

    private updateSessionZoneInfo(session: GridSession) {
        this.zoneAddress = session.zoneAddress || this.zoneAddress;
        this.zoneName = session.zoneName || this.zoneName;
        this.gpuType = session.gpuType || this.gpuType;
    }

    protected override isSessionSetupInProgress(): boolean {
        return !!this.getSessionRequestController;
    }

    /**
     * Emits GS_EVENT.SESSION_UPDATE event and the legacy GS_EVENT.PROGRESS_UPDATE
     * note: GFNClientSDK is using GS_EVENT.PROGRESS_UPDATE, instead of transitioning to GS_EVENT.SESSION_UPDATE they will switch to SessionControl usage
     * @param sessionUpdate
     */
    private sendSessionUpdateEvent(sessionUpdate: SessionUpdate): void {
        this.eventEmitter.emit(GS_EVENTS.SESSION_UPDATE, sessionUpdate);
        // legacy ProgressUpdateEvent which is required by GFNClientSDK.
        // GFNClient has migrated to SessionUpdate event.
        const eventData: SessionProgressUpdateEvent = {
            sessionId: sessionUpdate.sessionId,
            subSessionId: sessionUpdate.subSessionId,
            queuePosition: sessionUpdate.queuePosition,
            eta: sessionUpdate.eta,
            state: sessionUpdate.progressState
        };
        this.eventEmitter.emit(GS_EVENTS.PROGRESS_UPDATE, eventData);
    }
}

export class PassThruServer extends GridServer {
    constructor() {
        super();
    }

    getAllActiveSessions() {
        let sendEmptySessionList = () => {
            Log.d("{6bae601}", "{b575263}");
            let result: ActiveSessionResultEvent = {
                sessionList: []
            };
            this.eventEmitter.emit(GS_EVENTS.ACTIVE_SESSIONS_RESULT, result);
        };
        window.setTimeout(sendEmptySessionList, 1);
    }

    putOrPostSession(
        startParams: SessionParams,
        action: number,
        sessionId: string | undefined
    ): Promise<GridSession | undefined> {
        Log.d("{6bae601}", "{71c010f}");
        // todo: we should remove the resetSubSessionId map, no one clears the SubSessionId after a session completion.
        // the map just continues to grow!
        this.resetSubSessionId(getNewGuid(), startParams.sessionId ?? "PassThruSessionId");
        return new Promise((resolve, reject) => {
            window.setTimeout(() => {
                let session: GridSession = {
                    sessionId: this.getSessionId(),
                    subSessionId: this.getSubSessionId(),
                    appId: parseInt(startParams.appId),
                    state: SessionState.READY_FOR_CONNECTION,
                    signalConnectionInfo: { ip: "", port: 49100, protocol: "http" },
                    mediaConnectionInfo: [],
                    streamInfo: [],
                    appLaunchMode: startParams.appLaunchMode
                        ? startParams.appLaunchMode
                        : AppLaunchMode.Default,
                    zoneName: this.getZoneName(),
                    zoneAddress: this.getZoneAddress(),
                    gpuType: this.getGpuType(),
                    clientLocale: "",
                    finalizedStreamingFeatures: {}
                };
                if (this.serverAddress) {
                    session.signalConnectionInfo.ip = this.serverAddress;

                    if (startParams.connectionInfo) {
                        session.mediaConnectionInfo = startParams.connectionInfo;
                        for (const connectionInfo of session.mediaConnectionInfo) {
                            if (connectionInfo.usage === Usage.SIGNALING) {
                                session.signalConnectionInfo.ip = connectionInfo.ip;
                                session.signalConnectionInfo.port = connectionInfo.port;
                                session.signalConnectionInfo.protocol =
                                    connectionInfo.appLevelProtocol === AppLevelProtocol.HTTPS
                                        ? "https"
                                        : "http";
                                Log.d("{6bae601}", "{c6b8e0f}", connectionInfo.ip, connectionInfo.port);
                            }
                        }
                    }
                } else {
                    Log.e("{6bae601}", "{a43f57e}");
                }

                if (startParams.monitorSettings) {
                    for (const monitor of startParams.monitorSettings) {
                        session.streamInfo.push({
                            width: monitor.widthInPixels,
                            height: monitor.heightInPixels,
                            fps: monitor.framesPerSecond,
                            sdrHdrMode: monitor.sdrHdrMode
                        });
                    }
                } else {
                    for (const stream of startParams.streamParams!) {
                        session.streamInfo.push({
                            width: stream.width,
                            height: stream.height,
                            fps: stream.fps
                        });
                    }
                }
                Log.d("{6bae601}", "{07dc276}", session.sessionId);
                resolve(session);
            }, 1); // modify this value to show a longer loading screen.
        });
    }

    getSession(_sessionId: string, isPoll: boolean): Promise<GridSession | undefined> {
        return new Promise((resolve, reject) => {
            //its a bug if code reaches here.
            let err: ErrorDetails = { code: -1, description: "PassthruPollingNotSupported" };
            reject(err);
        });
    }

    sendDeleteRequest(sessionId: string): Promise<void> {
        return new Promise((resolve, reject) => {
            resolve();
        });
    }

    public updateAdState(adUpdate: AdUpdate): GsErrorCode {
        // not supported in passthru mode
        return <GsErrorCode>-1;
    }
}
